import { Injectable, signal, inject } from '@angular/core';
import { IngredientChoice } from '../models/menu/ingredient-choice.model';
import { OrderedItem } from '../models/ordering/ordered-item.model';
import { OrderableItem } from '../models/menu/orderable-item.model';
import { OrderedIngredient } from '../models/ordering/ordered-ingredient.model';
import { OrderedChoiceService } from './ordering/ordered-choice.service';

/**
 * Service for managing choice dependencies.
 * Builds a cycle‑pruned dependency map (ingredient → choices that depend on it)
 * and provides methods to add/remove choices with all their descendants.
 */
@Injectable({ providedIn: 'root' })
export class DependableIngredientMappingService {
  private orderedChoiceService = inject(OrderedChoiceService);

  // Map: ingredient name → list of choices that depend directly on that ingredient
  private dependableChoicesMap = signal<Map<string, IngredientChoice[]>>(new Map());

  /** Expose the map as a readonly signal */
  getDependableChoices() {
    return this.dependableChoicesMap.asReadonly();
  }

  /**
   * Build the dependency map, excluding any choices that are part of a cycle.
   * Must be called whenever the list of available IngredientChoice changes.
   */
  buildAcyclicDependableChoicesMap(choices: IngredientChoice[]): Map<string, IngredientChoice[]> {
    // 1. Build a map of which choices provide each ingredient
    const providersByIngredient = this.buildProvidersMap(choices);

    // 2. Build the directed dependency graph: choice → choices it depends on (prerequisites)
    const graph = this.buildDependencyGraph(choices, providersByIngredient);

    // 3. Find all choices that are part of any cycle
    const cycleNodes = this.findCycleNodes(graph);

    // 4. ADDITIONALLY: Find self-referential cycles where a choice depends on an ingredient it provides
    // E.g., "FINAL DEATH By PEPPERONI" depends on "PEPONI" AND contains "PEPONI" as an ingredient
    // This creates an infinite render loop: PEPONI → choice → ingredient PEPONI → choice again
    const selfRefCycles = new Set<string>();
    for (const choice of choices) {
      if (!choice.dependsOn) continue;
      
      // Check if this choice provides the ingredient it depends on
      const providesOwnDependency = choice.ingredients.some(
        ing => ing.name === choice.dependsOn
      );
      if (providesOwnDependency) {
        selfRefCycles.add(choice.name);
      }
    }

    // Combine both cycle sets
    const allCycleNodes = new Set([...cycleNodes, ...selfRefCycles]);

    // 5. Build the final map, skipping choices that are in cycles
    const safeMap = new Map<string, IngredientChoice[]>();
    for (const choice of choices) {
      if (!choice.dependsOn) continue;               // no dependency → not added to map
      if (allCycleNodes.has(choice.name)) continue;  // part of a cycle → excluded

      const ingredient = choice.dependsOn;
      if (!safeMap.has(ingredient)) {
        safeMap.set(ingredient, []);
      }
      safeMap.get(ingredient)!.push(choice);
    }

    this.dependableChoicesMap.set(safeMap);
    return safeMap;
  }

  /**
   * Build a map: ingredient name → list of choices that PROVIDE this ingredient.
   */
  private buildProvidersMap(choices: IngredientChoice[]): Map<string, string[]> {
    const providers = new Map<string, string[]>();
    for (const choice of choices) {
      for (const ing of choice.ingredients) {
        if (!providers.has(ing.name)) {
          providers.set(ing.name, []);
        }
        providers.get(ing.name)!.push(choice.name);
      }
    }
    return providers;
  }

  /**
   * Build the dependency graph: for each choice that has a `dependsOn`,
   * add edges to all choices that provide that ingredient.
   * Returns Map<choiceName, Set<prerequisiteChoiceName>>
   */
  private buildDependencyGraph(
    choices: IngredientChoice[],
    providersByIngredient: Map<string, string[]>
  ): Map<string, Set<string>> {
    const graph = new Map<string, Set<string>>();

    for (const choice of choices) {
      if (!choice.dependsOn) continue;

      const ingredient = choice.dependsOn;
      const providers = providersByIngredient.get(ingredient) ?? [];

      for (const providerName of providers) {
        // Do not add self‑dependency (a choice cannot depend on itself)
        if (providerName === choice.name) continue;

        if (!graph.has(choice.name)) {
          graph.set(choice.name, new Set());
        }
        graph.get(choice.name)!.add(providerName);
      }
    }
    return graph;
  }

  /**
   * Detect all nodes that belong to any cycle in the graph.
   * Uses DFS with three states: 0 = unvisited, 1 = visiting, 2 = visited.
   * When a back edge is found (neighbor state 1), marks every node from
   * the neighbor to the current node on the recursion stack as a cycle member.
   */
  private findCycleNodes(graph: Map<string, Set<string>>): Set<string> {
    const state = new Map<string, 0 | 1 | 2>(); // 0 unvisited, 1 visiting, 2 visited
    const stack: string[] = [];
    const cycleNodes = new Set<string>();

    const dfs = (node: string) => {
      state.set(node, 1);
      stack.push(node);

      const neighbors = graph.get(node) ?? [];
      for (const neighbor of neighbors) {
        if (!state.has(neighbor)) {
          dfs(neighbor);
        } else if (state.get(neighbor) === 1) {
          // Found a back edge: neighbor is currently on the stack → cycle detected
          // Mark all nodes from neighbor to current node as cycle members
          const idx = stack.indexOf(neighbor);
          for (let i = idx; i < stack.length; i++) {
            cycleNodes.add(stack[i]);
          }
        }
        // If neighbor state is 2, it's already processed, nothing to do
      }

      state.set(node, 2);
      stack.pop();
    };

    for (const node of graph.keys()) {
      if (!state.has(node)) {
        dfs(node);
      }
    }
    return cycleNodes;
  }

  // ------------------------------------------------------------------------
  // Public API for managing ordered items
  // ------------------------------------------------------------------------

  /**
   * Get all choices that directly depend on a given ingredient.
   */
  getDependents(ingredientName: string): IngredientChoice[] {
    return this.dependableChoicesMap().get(ingredientName) ?? [];
  }

  /**
   * Add a choice and all its descendants (choices that depend on it)
   * to the temporary order item. Also adds default ingredients.
   */
  addChoiceWithDescendants(
    choiceToAdd: IngredientChoice,
    tempItem: OrderedItem,
    orderableItem: OrderableItem
  ): void {
    // Use a queue to process all dependent choices breadth‑first
    const queue: IngredientChoice[] = [choiceToAdd];
    const processed = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (processed.has(current.name)) continue;
      processed.add(current.name);

      // 1. Add the current choice itself if not already present
      this.ensureChoiceInOrder(current, tempItem, orderableItem);

      // 2. Find all choices that depend on any ingredient provided by current choice
      for (const ingredient of current.ingredients) {
        const dependents = this.getDependents(ingredient.name);
        for (const dep of dependents) {
          if (!processed.has(dep.name)) {
            queue.push(dep);
          }
        }
      }
    }
  }

  /**
   * Ensure a specific choice is present in the ordered item.
   * If missing, create it and add its default ingredients.
   */
  private ensureChoiceInOrder(
    choice: IngredientChoice,
    tempItem: OrderedItem,
    orderableItem: OrderableItem
  ): void {
    const existing = tempItem.choices.find(c => c.name === choice.name);
    if (existing) return;

    const orderableChoice = orderableItem.getChoice(choice.name);
    if (!orderableChoice) return; // should not happen if data is consistent

    const newChoice = this.orderedChoiceService.createOrderedChoice(orderableChoice);
    // Add default ingredients
    for (const ing of orderableChoice.ingredients) {
      if (ing.isDefault) {
        const orderedIng = OrderedIngredient.createFromOrderable(ing);
        newChoice.ingredients.push(orderedIng);
      }
    }
    tempItem.choices.push(newChoice);
  }

  /**
   * Remove a choice and all its descendants (choices that depend on it)
   * from the temporary order item.
   */
  removeChoiceAndDescendants(
    tempItem: OrderedItem,
    choiceName: string,
    orderableItem: OrderableItem
  ): void {
    const orderableChoice = orderableItem.getChoice(choiceName);
    if (!orderableChoice) return;

    // Use a stack for depth‑first removal
    const toRemove = new Set<string>();
    const stack = [choiceName];

    while (stack.length > 0) {
      const name = stack.pop()!;
      if (toRemove.has(name)) continue;
      toRemove.add(name);

      const orderable = orderableItem.getChoice(name);
      if (!orderable) continue;

      // Find all choices that depend on any ingredient this choice provides
      for (const ingredient of orderable.ingredients) {
        const dependents = this.getDependents(ingredient.name);
        for (const dep of dependents) {
          if (!toRemove.has(dep.name)) {
            stack.push(dep.name);
          }
        }
      }
    }

    // Now remove all collected choices from tempItem
    tempItem.choices = tempItem.choices.filter(c => !toRemove.has(c.name));
  }

  /**
   * Remove all choices that depend on any of the given ingredients.
   * Used when an ingredient is deselected (e.g., in radio/enforced groups).
   */
  removeDependentChoices(
    tempItem: OrderedItem,
    ingredientNames: Set<string>,
    orderableItem: OrderableItem
  ): void {
    const choicesToRemove = new Set<string>();

    for (const ingName of ingredientNames) {
      const dependents = this.getDependents(ingName);
      for (const dep of dependents) {
        choicesToRemove.add(dep.name);
      }
    }

    // For each such choice, remove it and all its descendants
    for (const choiceName of choicesToRemove) {
      this.removeChoiceAndDescendants(tempItem, choiceName, orderableItem);
    }
  }
}